import { FC, useMemo } from 'react';
import { PieChart as RechartsPieChart, Pie, Cell } from 'recharts';
import { GridLegacy as Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardFooter } from './common/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from './common/chart-config';
import { CSVDownloader } from '../fileDownload/csv-downloader';
import { IChartOptions, isChartOptions } from './common/chart-options';
import { IChartProps } from './common/types';
import { useUserParams } from '../../context/user-params-context';
import { formatLabel } from './common/format-helpers';
import { ChartInfoTooltip } from './common/card-info-tooltip';
import { useChartData } from '../../hooks/useChartData';
import { useReleaseManagement, ReleaseFlag } from '@/utility/feature-management';
import { resolveChartTitle } from './common/resolve-chart-title';
import { usePreferences } from '../../utility/usePreferences';
import { capitalise } from '../../utility/utils';

interface DataItem {
  name: string;
  value: number;
  fill: string;
}
interface PieOption {
  fill?: string;
}

interface IPieChartOptions extends IChartOptions {
  pie?: { dataKey?: keyof DataItem; options?: Record<string, PieOption> };
}

export const PieChart: FC<IChartProps> = ({ uuid }) => {
  const { t } = useTranslation();
  const { userParams, parameterTypes } = useUserParams();
  const { distancePerHour, distanceUnitText, distanceUnitSingular } = usePreferences();

  const { data, isLoading } = useChartData<DataItem[], IPieChartOptions>(
    uuid,
    parameterTypes,
    userParams,
    { validateOptions: isChartOptions }
  );

  const releaseRefactorChartTitle = useReleaseManagement(
    ReleaseFlag.releaseRefactorChartTitle
  );

  const chartOptions = data?.options;
  const chartData = data?.data || [];
  const chartName = data?.name;
  const chartTranslationKey = data?.translationKey ?? undefined;

  const createChartConfig = (): ChartConfig => {
    if (!chartOptions?.pie?.options) return {};
    const config: ChartConfig = {};
    Object.entries(chartOptions.pie.options).forEach(([key, pieOptions]) => {
      config[key] = {
        label: key,
        color: pieOptions.fill ?? `var(--color-${key})`,
      };
    });
    return config;
  };

  const computedTitle = useMemo(() => {
    return resolveChartTitle({
      t,
      name: chartName ?? undefined,
      translationKey: chartTranslationKey,
      featureEnabled: releaseRefactorChartTitle,
      defaultText: chartName ?? undefined, // fallback to original name
      i18nParams: {
        distancePerHour,
        distanceUnit: capitalise(distanceUnitText),
        distanceUnitSingular: capitalise(distanceUnitSingular),
        timeFormat: 'hh:mm',
      },
    });
  }, [
    t,
    chartName,
    chartTranslationKey,
    releaseRefactorChartTitle,
    distancePerHour,
    distanceUnitText,
    distanceUnitSingular,
  ]);

  return (
    <Grid item sm={9} md={7} lg={5}>
      <Card
        style={{
          animation: isLoading ? 'pulse 2s linear infinite' : undefined,
        }}
      >
        <CardContent>
          {chartOptions?.description && (
            <ChartInfoTooltip description={chartOptions.description} />
          )}
          <ChartContainer config={createChartConfig()}>
            <RechartsPieChart>
              <Pie
                data={chartData}
                isAnimationActive={false}
                outerRadius={80}
                startAngle={90}
                endAngle={-360}
                label={(props) => {
                  const RAD = Math.PI / 180;
                  const {
                    cx,
                    cy,
                    midAngle,
                    innerRadius,
                    outerRadius,
                    payload,
                  } = props as any;
                  const r = 25 + innerRadius + (outerRadius - innerRadius);
                  const x =
                    cx + r * Math.cos(-(midAngle + Math.PI / 5) * RAD);
                  const y =
                    cy + r * Math.sin(-(midAngle + Math.PI / 5) * RAD);
                  return (
                    <text
                      x={x}
                      y={y}
                      fill="#000"
                      textAnchor={x > cx ? 'start' : 'end'}
                    >
                      <tspan>{payload.name} - </tspan>
                      <tspan fontWeight="bold">{payload.value}</tspan>
                    </text>
                  );
                }}
                dataKey={chartOptions?.pie?.dataKey || 'Value'}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    heading={chartOptions?.tooltip?.heading}
                    nameKey={chartOptions?.tooltip?.nameKey}
                    labelFormatter={(value) =>
                      formatLabel(userParams, {
                        ...chartOptions?.tooltip?.label,
                        value,
                      })
                    }
                  />
                }
              />
            </RechartsPieChart>
          </ChartContainer>
        </CardContent>
        <CardFooter>
          <Typography variant="subtitle1">
            {isLoading ? t('general.loading') : computedTitle}
          </Typography>
          {chartOptions?.enableCSVDownload && (
            <CSVDownloader data={chartData} filename={chartName || ''} />
          )}
        </CardFooter>
      </Card>
    </Grid>
  );
};

export default PieChart;
